import type { TFunction } from 'i18next';
import type { BadgeVariant } from '@/components/ui/badge';
import { formatDelayHours } from '@/lib/delay-limits/hours';
import { DELAY_POLICY_DEFAULTS } from '@/lib/delay-limits/policy';
import { formatFlightClock24, formatFlightDay } from '@/lib/disruptions/airport-tz';
import type { IDisruptionFlightStatus } from '@/lib/disruptions/types';
import { utcIsoToLocal, zoneFor } from './time';
import type { FlightDelayPhase, ICareTierVerdict, ICaseFlightDelay } from './types';

/**
 * The delay badge as a pure model (U1): which label, which tone, which numbers. The
 * component renders it; tests pin it. Labels are i18n KEYS of the `disruptions`
 * namespace — this module holds no language.
 *
 * Two sources feed the same badge:
 *   - a list row's `flightStatus@v1` (priority queue, Tower bar): the kind and the
 *     WORST delay seen (`maxDelayMinutes`, monotonic) — no STD/ETD on the row;
 *   - a case's timeline (`ICaseFlightDelay`, the disruption header): STD → ETD, the
 *     CURRENT delay, the phase and the "rebooking on hold" flag — or, once an operator
 *     released the hold, who released it.
 * Nothing known (no kind) ⇒ no badge at all — the empty state is "no badge".
 * The badge never speaks of care — what is owed lives in the delay panel (`careView`).
 * A provider report over the policy maximum adds «Fuera de política» (`outOfPolicy`); when
 * nothing was ever applied — a FIRST report over the maximum opens the timeline frozen:
 * no ETD, 0 min — the out-of-policy fact IS the label (never "+0 min" or an empty ETD).
 */
export interface IDelayBadgeModel {
  labelKey: string;
  /** Interpolation values of `labelKey`. */
  labelValues?: Record<string, string>;
  tone: BadgeVariant;
  /** Shown as "+X" when > 0. */
  delayMinutes?: number;
  /** STD → ETD (or ATD once departed), airport-local. */
  from?: string;
  to?: string;
  originIata?: string;
  /** `rebookingHold.active` — Nexa's rebooking waits for the flight (brief §11). */
  hold: boolean;
  /** The hold was released by hand (`rebookingHold.releasedBy`) and is no longer active. */
  released?: { name?: string };
  /** Other flights of the disruption that are also delayed / cancelled. */
  more: number;
  /** A provider report exceeds the policy maximum, not applied (delay-limits) — the chip. */
  outOfPolicy?: boolean;
}

/** The labels that already say «out of policy» (no chip next to them). */
export function leadsOutOfPolicy(model: IDelayBadgeModel): boolean {
  return model.labelKey.startsWith('delay.badge.outOfPolicy');
}

const ESCALATED: ReadonlySet<FlightDelayPhase> = new Set(['ESCALATED_GRACE', 'ESCALATED_RELEASED']);

export function isEscalated(phase: FlightDelayPhase | undefined): boolean {
  return !!phase && ESCALATED.has(phase);
}

/** The badge of a list row's `flightStatus@v1`, or `null` when there is nothing to say. */
export function badgeFromFlightStatus(
  status: IDisruptionFlightStatus | null | undefined,
): IDelayBadgeModel | null {
  const model = statusBadge(status);
  if (!model || !status?.outOfPolicy) return model;
  // A delay with nothing applied yet (a first report over the maximum): the flag leads.
  return model.labelKey === 'delay.badge.kind.DELAY' && !model.delayMinutes
    ? { ...model, labelKey: 'delay.badge.outOfPolicy', tone: 'error', outOfPolicy: true }
    : { ...model, outOfPolicy: true };
}

function statusBadge(status: IDisruptionFlightStatus | null | undefined): IDelayBadgeModel | null {
  if (!status?.kind) return null;
  const max = typeof status.maxDelayMinutes === 'number' ? status.maxDelayMinutes : undefined;
  if (status.escalated) {
    // M2 placeholder-safe: the grace countdown is U5's; here only the fact.
    return { labelKey: 'delay.badge.state.escalated', tone: 'error', ...(max ? { delayMinutes: max } : {}), hold: false, more: 0 };
  }
  if (status.kind === 'CANCELLED') return { labelKey: 'delay.badge.kind.CANCELLED', tone: 'error', hold: false, more: 0 };
  if (status.kind === 'DIVERTED') return { labelKey: 'delay.badge.kind.DIVERTED', tone: 'warning', hold: false, more: 0 };
  return { labelKey: 'delay.badge.kind.DELAY', tone: 'warning', ...(max ? { delayMinutes: max } : {}), hold: false, more: 0 };
}

/** The badge of one case's timeline (`more` = other affected flights of the disruption). */
export function badgeFromDelay(delay: ICaseFlightDelay, more = 0): IDelayBadgeModel {
  const model = delayBadge(delay, more);
  const report = delay.outOfPolicy;
  if (!report) return model;
  // A first report over the maximum opens the timeline frozen (no ETD, 0 min): it leads.
  return delay.phase === 'DELAYED' && delay.maxDelayMinutes === 0
    ? {
        ...model,
        labelKey: 'delay.badge.outOfPolicyReported',
        labelValues: { delay: formatDelayHours(report.delayMinutes), limit: formatDelayHours(report.limitMinutes) },
        tone: 'error',
        outOfPolicy: true,
      }
    : { ...model, outOfPolicy: true };
}

function delayBadge(delay: ICaseFlightDelay, more: number): IDelayBadgeModel {
  const hold = delay.rebookingHold?.active === true;
  const releasedBy = hold ? undefined : delay.rebookingHold?.releasedBy;
  const base = {
    hold,
    more,
    ...(releasedBy ? { released: releasedBy.name ? { name: releasedBy.name } : {} } : {}),
    ...(delay.originIata ? { originIata: delay.originIata } : {}),
  };
  const minutes = delay.delayMinutes > 0 ? { delayMinutes: delay.delayMinutes } : {};
  if (isEscalated(delay.phase)) return { ...base, ...minutes, labelKey: 'delay.badge.state.escalated', tone: 'error' };
  if (delay.currentKind === 'CANCELLED') return { ...base, labelKey: 'delay.badge.kind.CANCELLED', tone: 'error' };
  if (delay.currentKind === 'DIVERTED') return { ...base, labelKey: 'delay.badge.kind.DIVERTED', tone: 'warning' };
  if (delay.phase === 'DEPARTED') {
    return {
      ...base,
      ...minutes,
      labelKey: 'delay.badge.state.DEPARTED',
      tone: 'neutral',
      from: delay.scheduledDepartureAt,
      ...(delay.actualDepartureAt ? { to: delay.actualDepartureAt } : {}),
    };
  }
  if (delay.phase === 'RECOVERED') return { ...base, labelKey: 'delay.badge.state.RECOVERED', tone: 'muted' };
  return {
    ...base,
    ...minutes,
    labelKey: 'delay.badge.kind.DELAY',
    tone: 'warning',
    from: delay.scheduledDepartureAt,
    ...(delay.estimatedDepartureAt ? { to: delay.estimatedDepartureAt } : {}),
  };
}

/** Urgency of a phase for picking the timeline a disruption-level badge shows. */
const PHASE_RANK: Readonly<Record<FlightDelayPhase, number>> = {
  ESCALATED_GRACE: 4,
  ESCALATED_RELEASED: 3,
  DELAYED: 2,
  DEPARTED: 1,
  RECOVERED: 0,
};

/**
 * The timeline a disruption-level badge shows: the most urgent phase, then the
 * largest current delay. `others` = the remaining flights still delayed / escalated.
 */
export function leadDelay(delays: readonly ICaseFlightDelay[]): { lead: ICaseFlightDelay; others: number } | null {
  if (delays.length === 0) return null;
  // Ties go to the flight that leaves first, then a fixed identity: with six flights all
  // +4 h, the order the reads arrived in picked the lead, so it hopped between them on
  // every refresh — the header's time and the copilot's flight changing under the
  // operator, and «Cambió la hora» said for a change nobody made (PO 2026-11-03).
  const sorted = [...delays].sort(
    (a, b) =>
      PHASE_RANK[b.phase] - PHASE_RANK[a.phase] ||
      b.delayMinutes - a.delayMinutes ||
      a.scheduledDepartureAt.localeCompare(b.scheduledDepartureAt) ||
      a.caseUrn.localeCompare(b.caseUrn),
  );
  const lead = sorted[0]!;
  const others = sorted.slice(1).filter((d) => d.phase === 'DELAYED' || isEscalated(d.phase)).length;
  return { lead, others };
}

/** "+X" as an i18n key + values: `+45 min`, `+2 h`, `+2 h 10 min`. */
export function delayParts(minutes: number): { key: string; h: number; m: number } {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return { key: 'delay.badge.minutes', h, m };
  return { key: m === 0 ? 'delay.badge.hours' : 'delay.badge.hoursMinutes', h, m };
}

/** "+45 min" / "+2 h" / "+2 h 10 min", rendered with the `disruptions` namespace. */
export function formatDelay(t: TFunction<'disruptions'>, minutes: number): string {
  const { key, h, m } = delayParts(minutes);
  return t(key, { h, m });
}

/**
 * An instant as the departures board reads it (airport-local 24 h), with the day added
 * when it is not the reference's day (a delay that crosses midnight: "23:25 → 4 Jun 02:10").
 */
export function clockOf(iso: string, originIata: string | undefined, referenceIso?: string): string {
  const clock = formatFlightClock24(iso, originIata);
  if (!referenceIso) return clock;
  const day = formatFlightDay(iso, originIata);
  return day !== formatFlightDay(referenceIso, originIata) ? `${day} ${clock}` : clock;
}

/**
 * What a delay owes, as the delay panel says it. A delay is care: meals by delay length,
 * and a hotel ONLY when the policy's next-day rule granted one (`care.nextDayHotel`,
 * delay-limits — the new departure is the next local day at or after the policy's hour;
 * monotonic). Otherwise an overnight verdict (`overnight`, or a legacy `kind: 'HOTEL'` /
 * `OVERNIGHT_GAP` from a producer that still classifies it so) is surfaced as a FACT
 * ("overnight — meals only, no hotel"), never as a hotel benefit.
 */
export interface ICareView {
  /** Meals owed (0 = nothing owed yet). */
  meals: number;
  mealPerPax?: { amount: number; currency: string };
  /** The wait runs through the night — informational only. */
  overnight: boolean;
  /** The rule's hotel: the threshold's local hour at the departure airport; `null` = meals only. */
  hotelHour: number | null;
}

export function careView(
  care: Pick<
    ICareTierVerdict,
    'kind' | 'reason' | 'overnight' | 'mealTiers' | 'mealPerPax' | 'nextDayHotel' | 'hotelThresholdAt'
  >,
  originIata?: string,
): ICareView {
  const meals = Math.max(0, Math.floor(care.mealTiers ?? 0));
  const threshold = care.hotelThresholdAt ? utcIsoToLocal(care.hotelThresholdAt, zoneFor(originIata)) : '';
  return {
    meals,
    ...(meals > 0 && care.mealPerPax ? { mealPerPax: care.mealPerPax } : {}),
    overnight: care.overnight === true || care.kind === 'HOTEL' || care.reason === 'OVERNIGHT_GAP',
    hotelHour: !care.nextDayHotel
      ? null
      : threshold
        ? Number(threshold.slice(11, 13))
        : DELAY_POLICY_DEFAULTS.nextDayHotelAfterLocalHour,
  };
}

/** The PNR's record locator out of its URN (`urn:pnr:ABC123:vendor:x` → `ABC123`) — the friendly label. */
export function locatorOf(pnrUrn: string): string {
  return pnrUrn.replace(/^urn:pnr:/, '').split(':')[0] || pnrUrn;
}

/** A flight of the disruption the delay panel can show / declare for. */
export interface IDelayFlightOption {
  caseUrn: string;
  flightUrn: string;
  /** "LA288 · SCL→GRU" — friendly, never the URN when the flight is known. */
  label: string;
  originIata?: string;
}

/** The disruption's flights (its cases that carry one), plus any timeline the cases list lacks. */
export function flightOptionsOf(
  cases: ReadonlyArray<{ caseUrn: string; flightUrn?: string; flightNumber?: string; origin?: string; destination?: string }>,
  delays: readonly ICaseFlightDelay[] = [],
): IDelayFlightOption[] {
  const out: IDelayFlightOption[] = [];
  const seen = new Set<string>();
  for (const c of cases) {
    if (!c.flightUrn || seen.has(c.caseUrn)) continue;
    seen.add(c.caseUrn);
    const route = c.origin && c.destination ? `${c.origin}→${c.destination}` : null;
    out.push({
      caseUrn: c.caseUrn,
      flightUrn: c.flightUrn,
      label: [c.flightNumber, route].filter(Boolean).join(' · ') || c.flightUrn,
      ...(c.origin ? { originIata: c.origin } : {}),
    });
  }
  for (const d of delays) {
    if (seen.has(d.caseUrn)) continue;
    seen.add(d.caseUrn);
    out.push({ caseUrn: d.caseUrn, flightUrn: d.flightUrn, label: d.flightUrn, ...(d.originIata ? { originIata: d.originIata } : {}) });
  }
  return out;
}
